/* eslint-disable tsdoc/syntax -- ignore doc syntax for file */

import {
    type ApolloClient,
    type ApolloQueryResult,
    type NormalizedCacheObject,
    type OperationVariables,
    type QueryOptions,
} from '@apollo/client';
import type {
    IServerSideDatasource,
    IServerSideGetRowsParams,
    IServerSideGetRowsRequest,
    LoadSuccessParams,
    SortModelItem,
} from '@ag-grid-community/core';
import apolloClient from '@api/client';
import { OrderDirection } from '@gql/graphql';
import {
    convertFilterModelToGraphQLWhereStatement,
    type FilterModels,
    mapToObjectRec,
} from './graphql-conversion';

export type ServerSideVariables = {
    [key: string]:
        | string
        | number
        | boolean
        | Date
        | ServerSideVariables
        | ServerSideVariables[]
        | string[];
} | null;

interface ServerSideDatasourceStrategy {
    fetchData: (params: IServerSideGetRowsParams) => void;
}

/**
 * This class is a wrapper around the ag-grid datasource interface.
 * It allows us to inject a strategy for fetching data from a server.
 */
export class Datasource implements IServerSideDatasource {
    strategy: ServerSideDatasourceStrategy;

    constructor(strategy: ServerSideDatasourceStrategy) {
        this.strategy = strategy;
    }

    getRows(params: IServerSideGetRowsParams): void {
        this.strategy.fetchData(params);
    }
}

/**
 * Helpers
 */

function mapSortDirection(dir: string): string {
    switch (dir) {
        case 'asc':
            return OrderDirection.Asc;
        case 'desc':
            return OrderDirection.Desc;
        default:
            return OrderDirection.Asc;
    }
}

export interface GraphQLDataSourceOptions
    extends Pick<QueryOptions, 'query' | 'variables'> {
    columnSortMap?: (columnId: string) => string;
    columnFilterMap?: (model: FilterModels) => ServerSideVariables;
    /**
     * If true, the datasource will only fetch the requested amount of data but not more. Essentially disabling pagination.
     */
    hardLimit?: number;
    /**
     * If true, additional logs will be printed to the console. (default: false)
     */
    debug?: boolean;
    /**
     * If true, rowCount is not reported upfront — AG Grid only knows about loaded rows.
     * Rows load progressively as the user scrolls, with a properly-sized scrollbar.
     */
    infiniteScroll?: boolean;
    /**
     * Preloaded initial data for the first page (startRow === 0) to avoid redundant network requests.
     */
    initialData?: GraphQLDataSourceCallbackResult;
    /**
     * Opt-in: paginate using Relay cursors (`first`/`after`, falling back to
     * `last` for a direct jump to the final page) instead of `hardLimit`/a
     * flat `first`. rowCount is reported as the connection's `totalCount`
     * with no cap. Requires the callback to return `pageInfo.endCursor` on
     * every page. Only grids that opt in are affected; without this flag
     * the existing hardLimit/first behavior is unchanged.
     */
    cursorPagination?: boolean;
}

export interface GraphQLDataSourceConnectionPageInfo {
    endCursor?: string | null;
    startCursor?: string | null;
}

export interface GraphQLDataSourceCallbackResult {
    rows: Record<string, string | number | boolean | Date>[] | unknown[];
    totalCount?: number;
    /**
     * Relay connection page info, required when `cursorPagination` is enabled.
     */
    pageInfo?: GraphQLDataSourceConnectionPageInfo;
}

type GraphQLDataSourceCallback<T> = (
    response: ApolloQueryResult<T>,
    query: QueryOptions<OperationVariables, T>,
) => GraphQLDataSourceCallbackResult;

/**
 * This class is a concrete implementation of the ServerSideDatasourceStrategy interface.
 * It uses the ApolloClient to fetch data from a GraphQL server.
 * You specify the query options and a callback to handle the successful response.
 * The callback is responsible for returning the rows of data to ag-grid.
 *
 * The GraphQL query must accept the following parameters:
 * - first: number (the number of rows to fetch)
 * - orderBy: { field: string, direction: OrderDirection } (the sorting mechanism)
 * - where: { [key: string]: string | number | boolean | Date } (the filtering mechanism)
 */

interface FilterParameters {
    first?: number;
    after?: string;
    last?: number;
    orderBy?: { field: string; direction: string };
    where?: ServerSideVariables;
}

interface CursorPaginationPlan {
    variables: Pick<FilterParameters, 'first' | 'after' | 'last'>;
    /**
     * Set only for the rare arbitrary-jump fallback: the full response must
     * be sliced client-side to the requested block.
     */
    slice?: { start: number; end: number };
}

export class GraphQLDataSourceStrategy<T>
    implements ServerSideDatasourceStrategy
{
    // The ApolloClient instance
    client: ApolloClient<NormalizedCacheObject>;
    // The set of options that applies to the query and other things
    options: GraphQLDataSourceOptions;
    // The callback that is responsible for handling the response and returning the data to ag-grid
    callback: GraphQLDataSourceCallback<T>;
    // Preloaded initial data for the first page
    private initialData?: GraphQLDataSourceCallbackResult;
    // cursorPagination only: block start row -> `after` cursor of the
    // previous block (the endCursor of the block ending at that row).
    private cursorsByStartRow = new Map<number, string>();
    // cursorPagination only: most recently known connection totalCount,
    // used to detect a jump to the final page.
    private lastKnownTotalCount?: number;
    // cursorPagination only: signature of the last request's sort/filter
    // model, used to invalidate cached cursors when the row ordering changes.
    private cursorPaginationSignature?: string;

    constructor(
        options: GraphQLDataSourceOptions,
        callback: GraphQLDataSourceCallback<T>,
    ) {
        this.client = apolloClient;
        this.options = options;
        this.callback = callback;
        this.initialData = options.initialData;
    }

    /**
     * Fetch the data from the GraphQL API.
     * It will either call the success or fail callback depending on the result.
     */
    fetchData(params: IServerSideGetRowsParams<T>): void {
        const isInitialPage =
            (params.request.startRow ?? 0) === 0 &&
            params.request.sortModel.length === 0 &&
            Object.keys(params.request.filterModel ?? {}).length === 0;

        if (this.initialData && isInitialPage) {
            const { rows, totalCount, pageInfo } = this.initialData;
            this.initialData = undefined;

            const rowCount = this.resolveRowCount(
                rows.length,
                totalCount,
                params.request,
            );

            if (this.options.cursorPagination) {
                this.lastKnownTotalCount = totalCount;
                if (pageInfo?.endCursor) {
                    this.cursorsByStartRow.set(rows.length, pageInfo.endCursor);
                }
            }

            const successArguments: LoadSuccessParams = {
                rowData: rows,
                rowCount,
                groupLevelInfo: {
                    totalCount,
                },
            };

            params.success(successArguments);
            return;
        }

        if (this.options.cursorPagination) {
            this.syncCursorPaginationSignature(params.request);
        }

        const { variables, slice } = this.setQueryVariables(params.request);
        const queryOptions: QueryOptions<OperationVariables, T> = {
            query: this.options.query,
            variables,
            fetchPolicy: 'no-cache',
        };

        this.client
            .query<T>(queryOptions)
            .then((response) => {
                const {
                    rows: allRows,
                    totalCount,
                    pageInfo,
                } = this.callback(response, queryOptions);
                const rows = slice
                    ? allRows.slice(slice.start, slice.end)
                    : allRows;

                if (this.options.cursorPagination) {
                    this.lastKnownTotalCount = totalCount;
                    const endRow = params.request.endRow;
                    if (typeof endRow === 'number' && pageInfo?.endCursor) {
                        this.cursorsByStartRow.set(endRow, pageInfo.endCursor);
                    }
                }

                const rowCount = this.resolveRowCount(
                    rows.length,
                    totalCount,
                    params.request,
                );

                // Set up the success arguments for ag-grid
                const successArguments: LoadSuccessParams = {
                    rowData: rows,
                    rowCount,
                    groupLevelInfo: {
                        totalCount,
                    },
                };

                // Call the success callback
                params.success(successArguments);
            })
            .catch((_: unknown) => {
                // Should the query fail, we notify ag-grid of the failure
                params.fail();
            });
    }

    /**
     * Map the ag-grid sort model to what the GraphQL API expects.
     * You can optionally provide a columnSortMap function to map the columnId to the GraphQL field name.
     * The direction is mapped to the GraphQL OrderDirection enum.
     */
    private mapSortModel(
        sortModel: SortModelItem[],
    ): { field: string; direction: string }[] {
        return sortModel.map((srt) => ({
            field:
                typeof this.options.columnSortMap === 'function'
                    ? this.options.columnSortMap(srt.colId)
                    : srt.colId.toUpperCase(),
            direction: mapSortDirection(srt.sort),
        }));
    }

    /**
     * Map the ag-grid filter model to what the GraphQL API expects.
     * You can optionally provide a columnFilterMap function to map the columnId to the GraphQL field name.
     * The filter type is mapped to the GraphQL FilterType enum.
     */
    private mapFilterModel(
        filterModel: FilterModels | null,
    ): ServerSideVariables {
        if (!filterModel) {
            return null;
        }
        // Allow the user to provide a custom filtering mapping function
        if (typeof this.options.columnFilterMap === 'function') {
            return this.options.columnFilterMap(filterModel);
        }

        const whereStatement =
            convertFilterModelToGraphQLWhereStatement(filterModel);
        return mapToObjectRec(whereStatement);
    }

    /**
     * Set the query variables based on the ag-grid request.
     */
    private setQueryVariables(request: IServerSideGetRowsRequest): {
        variables: FilterParameters;
        slice?: { start: number; end: number };
    } {
        const { startRow, endRow } = request;

        // Copy the variables from the options
        const variables: FilterParameters = {
            ...this.options.variables,
        };
        let slice: { start: number; end: number } | undefined;

        if (this.options.cursorPagination) {
            const plan = this.buildCursorPaginationRequest(
                startRow ?? 0,
                endRow ?? 0,
            );
            Object.assign(variables, plan.variables);
            slice = plan.slice;
        } else if (
            // Slice the data based on the ag-grid request
            // (e.g., startRow = 0, endRow = 100 -> first = 100)
            typeof startRow === 'number' &&
            typeof endRow === 'number' &&
            !this.options.hardLimit
        ) {
            variables.first = endRow - startRow;
        } else if (this.options.hardLimit) {
            variables.first = this.options.hardLimit;
        } else {
            variables.first = 100;
        }

        // Apply the sorting mechanism that GraphQL API expects
        const sortModel = this.mapSortModel(request.sortModel);
        if (sortModel.length > 0) {
            variables.orderBy = sortModel[0]; // TODO: support multiple sort, if possible in the API
        }

        // Apply the filter mechanism that GraphQL API expects
        const where = this.mapFilterModel(request.filterModel);

        if (where && Object.entries(where).length > 0) {
            variables.where = { ...variables.where, ...where };
        }

        return { variables, slice };
    }

    /**
     * cursorPagination only: decide how to page the requested block using
     * Relay cursors.
     * - startRow 0: `first` only, no cursor needed.
     * - a cached cursor exists for this block's start row (the previous
     *   block's endCursor): `first` + `after`.
     * - no cached cursor, but the block reaches/exceeds the known
     *   totalCount (a direct jump to the final page): `last` from the end,
     *   which AG Grid's SSRM only requests contiguously with row order
     *   preserved, so no re-sorting is needed.
     * - otherwise (an arbitrary jump with no cached cursor, e.g. a page
     *   restored after a reload): fetch `first: endRow` from the start and
     *   slice the requested block out client-side. This over-fetches, but
     *   such jumps are rare — AG Grid normally requests sequential blocks.
     */
    private buildCursorPaginationRequest(
        startRow: number,
        endRow: number,
    ): CursorPaginationPlan {
        const count = Math.max(endRow - startRow, 0);

        if (startRow === 0) {
            return { variables: { first: count } };
        }

        const afterCursor = this.cursorsByStartRow.get(startRow);
        if (afterCursor) {
            return { variables: { first: count, after: afterCursor } };
        }

        if (
            typeof this.lastKnownTotalCount === 'number' &&
            endRow >= this.lastKnownTotalCount
        ) {
            return {
                variables: {
                    last: Math.max(this.lastKnownTotalCount - startRow, 0),
                },
            };
        }

        return {
            variables: { first: endRow },
            slice: { start: startRow, end: endRow },
        };
    }

    /**
     * cursorPagination only: clear cached cursors and the known totalCount
     * whenever the sort/filter model changes, since cached cursors are only
     * valid for the row ordering they were fetched under.
     */
    private syncCursorPaginationSignature(
        request: IServerSideGetRowsRequest,
    ): void {
        const signature = JSON.stringify({
            sortModel: request.sortModel,
            filterModel: request.filterModel,
        });

        if (
            this.cursorPaginationSignature !== undefined &&
            this.cursorPaginationSignature !== signature
        ) {
            this.cursorsByStartRow.clear();
            this.lastKnownTotalCount = undefined;
        }

        this.cursorPaginationSignature = signature;
    }

    /**
     * Resolve the rowCount reported to ag-grid for a response, dispatching
     * between the cursorPagination, infiniteScroll and default/hardLimit
     * modes.
     */
    private resolveRowCount(
        rowsReturned: number,
        totalCount: number | undefined,
        request: IServerSideGetRowsRequest,
    ): number | undefined {
        if (this.options.cursorPagination) {
            return totalCount ?? rowsReturned;
        }

        if (this.options.infiniteScroll) {
            return this.calculateInfiniteScrollRowCount(
                rowsReturned,
                request.startRow ?? 0,
                request.endRow ?? rowsReturned,
            );
        }

        if (typeof totalCount === 'number') {
            return this.calculateRowCount(totalCount);
        }

        return rowsReturned;
    }

    private calculateRowCount(totalCount?: number): number {
        return Math.min(this.options.hardLimit ?? Infinity, totalCount ?? 0);
    }

    private calculateInfiniteScrollRowCount(
        rowsReturned: number,
        startRow: number,
        endRow: number,
    ): number | undefined {
        const requested = endRow - startRow;
        if (rowsReturned < requested) {
            return startRow + rowsReturned;
        }
        return undefined as unknown as number;
    }
}
